import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { DropdownMenu } from "components/dropdownMenu";
import { ISeatLayoutDropdownItem, ISeatLayoutForm, ISeatLayoutInfoForm, ISimpleEventDetail } from "interfaces";
import { isEmpty } from "lodash";
import { useState } from "react";
import { ConfirmModal } from "components/confirmModal";
import { HttpError, useCreate, useOne, useParse, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { SeatLayoutModal } from "./seatLayoutModal";
import { HistoryLodModal } from "components/historyLogModal";
import { UpdateSeatLayoutInfoModal } from "./updateSeatLayoutInfoModal";
import { DuplicateSeatLayoutModal } from "./duplicateSeatLayoutModal";

const INITIAL_SEAT_LAYOUT_CONFIG = {
  open: false,
  id: "",
  submit: (data: any) => {},
};

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {}
}

interface SeatLayoutProps {
  eventDetailData: ISimpleEventDetail;
}
export const SeatLayout = ({eventDetailData}: SeatLayoutProps) => {
  const parse = useParse();
  const { params } = parse();

  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);

  const [seatLayoutModalConfig, setSeatLayoutModalConfig] = useState(INITIAL_SEAT_LAYOUT_CONFIG);
  const [seatLayoutInfoModalConfig, setSeatLayoutInfoModalConfig] = useState(INITIAL_SEAT_LAYOUT_CONFIG);

  const [duplicateSeatLayoutModalConfig, setDuplicateSeatLayoutModalConfig] = useState(INITIAL_SEAT_LAYOUT_CONFIG);

  const [activePhaseStatusModalConfig, setActivePhaseStatusModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const { mutate: createMutation, isLoading: createMutationLoading } = useCreate();
  const { mutate: updateMutation } = useUpdate();
  
  const { data: seatLayoutData, refetch: refetchSeatLayout, isFetching: isFetchingLayout } = useOne<ISeatLayoutDropdownItem[], HttpError>({
    resource: "content/dropdown",
    id: "layout-seat",
    meta: {
      resource: "data",
      filters: [{ field: "event_id", operator: "eq", value: eventDetailData.id },
      ]
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const seatLayouts = seatLayoutData?.data ?? null;
  
  const _addSeatLayout = (data: ISeatLayoutForm, callbackMessage?: string) => {
    createMutation(
      {
        resource: "content/seat-maps/layout-seat",
        meta: {
          ignoreResourceWrapper: true
        },
        values: {
          ...data,
          event_id: eventDetailData.id
        },
      },
      {
        onSuccess: (data) => {
          refetchSeatLayout();
          toast.success(callbackMessage ?? "Add seat layout successfully");
        },
      }
    );
  };

  const _updateSeatlayoutInfo = (data: ISeatLayoutInfoForm, id: string) => {
    updateMutation(
      {
        resource: "content/seat-maps/layout-seat",
        id,
        meta: {
          ignoreResourceWrapper: true
        },
        values: data,
      },
      {
        onSuccess: (data) => {
          refetchSeatLayout();
          toast.success("Update seat layout info successfully");
        },
      }
    );
  };


  const _onCloseModal = () => {
    setSeatLayoutModalConfig(INITIAL_SEAT_LAYOUT_CONFIG);
  }
  
  const _onCloseInfoModal = () => {
    setSeatLayoutInfoModalConfig(INITIAL_SEAT_LAYOUT_CONFIG);
  }

  const _onCloseDuplicateModal = () => {
    setDuplicateSeatLayoutModalConfig(INITIAL_SEAT_LAYOUT_CONFIG);
  }

  const _onCloseActivePhaseModal = () => {
    setActivePhaseStatusModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      title: activePhaseStatusModalConfig.title
    });
  }

  return  (
    <div className="box-container p-4">
      <div>
        <div className="grid gap-y-4 sm:grid-cols-6">
          {(seatLayouts ?? []).map((layout: {id: string; layout_name: string}, index: number) => (
            <div 
              className="col-span-full flex flex-row items-start justify-between rounded-md border border-gray-300 px-4 py-3" 
              key={layout.id}
            >
              <div className="flex flex-row items-center flex-wrap">
                <p>{layout.layout_name}</p>
              </div>
              <div className="self-center">
                <DropdownMenu
                  label={
                    <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                  }
                  items={[
                    {
                      label: "Edit",
                      onClick: () => setSeatLayoutInfoModalConfig({
                        open: true,
                        id: layout.id,
                        submit: (data) => _updateSeatlayoutInfo(data, layout.id)
                      }),
                    },
                    {
                      label: "Duplicate",
                      onClick: () => setDuplicateSeatLayoutModalConfig({
                        open: true,
                        id: layout.id,
                        submit: (data) => _addSeatLayout(data, "Duplicate Successfully")
                      })
                    },
                    {
                      label: "Log",
                      onClick: () => setOpenLogModalId(layout.id ?? "")
                    }
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="text-center text-sm">
          {isEmpty(seatLayouts) ? <p>No seat layout</p> : <></>}
        </div>
      </div>
      <div className="add_edit_layout">
        <div className="w-full text-center">
          <button
            type="button"
            className="flex flex-row items-center mx-auto mt-4 w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
            onClick={() => setSeatLayoutModalConfig({
              open: true,
              id: "",
              submit: (data) => _addSeatLayout(data)
            })}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
            <span className="shrink-0 text-sm">Add new seat layout</span>
          </button>
        </div>
        
        <SeatLayoutModal 
          open={seatLayoutModalConfig.open}
          onClose={_onCloseModal}
          id={seatLayoutModalConfig.id}
          onSubmit={seatLayoutModalConfig.submit}
        />
        <UpdateSeatLayoutInfoModal
          eventDetailData={eventDetailData}
          open={seatLayoutInfoModalConfig.open}
          onClose={_onCloseInfoModal}
          id={seatLayoutInfoModalConfig.id}
          onSubmit={seatLayoutInfoModalConfig.submit}
        />
        <DuplicateSeatLayoutModal
          open={duplicateSeatLayoutModalConfig.open}
          onClose={_onCloseDuplicateModal}
          id={duplicateSeatLayoutModalConfig.id}
          onSubmit={duplicateSeatLayoutModalConfig.submit}
        />
        <ConfirmModal 
          key="active-phase"
          title={activePhaseStatusModalConfig.title}
          content=""
          open={activePhaseStatusModalConfig.open}
          onConfirm={activePhaseStatusModalConfig.submit}
          onClose={_onCloseActivePhaseModal}
        />
        <HistoryLodModal
          resource={`content/seat-maps/layout-seat/${openLogModalId}/history`}
          open={openLogModalId ? true : false}
          onClose={() => setOpenLogModalId(null)}
          key="seat-layout"
          id="seat-layout"
        />
      </div>
    </div>
  );
}